import React, { useEffect, useState } from "react";
import { RefreshCw, Loader2, Brain } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "—");

// Continuous self-learning cycle — proof the loop is alive: what was learned, graded, and the learned blend weight.
export const LearningCycleCard = () => {
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/learning/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const runNow = async () => {
    setRunning(true);
    try { const r = await api.post("/learning/run", {}, { timeout: 280000 }); toast.success(`Cycle done — ${r.data?.hebbian?.learned ?? 0} new observations learned, ${r.data?.graded ?? 0} predictions graded`); }
    catch { toast.error("Learning cycle failed"); }
    finally { setRunning(false); load(); }
  };
  const last = st?.last_result?.hebbian || {};
  const state = st?.never_run ? "idle" : st?.running || running ? "loading" : "ready";
  return (
    <div className="card-tac mb-4 p-5" data-testid="learning-cycle-card"
      data-octon-section="self learning cycle continuous hebbian grading brier blend weight" data-octon-label="Self-Learning Cycle" data-octon-state={state}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Brain size={16} className="txt-cyan" />
          <span className="font-tac text-[10px] tracking-widest text-white/60">SELF-LEARNING CYCLE · EVERY {st?.cycle_every_hours ?? 6}H</span>
          <span className={`rounded-md px-2 py-0.5 font-tac text-[9px] tracking-widest ${st?.never_run ? "bg-amber-400/15 text-amber-300" : "bg-lime-400/15 text-lime-300"}`} data-testid="learning-cycle-state">
            {st?.never_run ? "NOT YET RUN" : st?.running ? "RUNNING" : "ALIVE"}
          </span>
        </div>
        <button onClick={runNow} disabled={running} data-testid="learning-run-now"
          className="inline-flex items-center gap-2 rounded-xl border border-white/12 px-3 py-1.5 text-xs text-white/80 hover:border-cyan-400/40 disabled:opacity-50">
          {running ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Learn now
        </button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 text-sm">
        {[
          ["Last cycle", fmt(st?.last_finished)],
          ["New observations", last.learned ?? "—"],
          ["Matches absorbed (total)", st?.total_learned_fixtures ?? "—"],
          ["Predictions graded", `${st?.graded_total ?? 0}${st?.hit_rate_pct != null ? ` · ${st.hit_rate_pct}% hit` : ""}`],
          ["Learned DC blend", st?.blend_w != null ? `${Math.round(st.blend_w * 100)}% engine${st?.graded_total >= 10 ? "" : " (default until 10 graded)"}` : "—"],
        ].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-white/8 bg-black/30 p-3" data-testid={`learning-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-tac text-[9px] tracking-widest text-white/40">{k.toUpperCase()}</p>
            <p className="mt-1 font-mono text-sm text-white/90">{String(v)}</p>
          </div>
        ))}
      </div>
      {st?.octon_brier != null && <p className="mt-2 text-[11px] text-white/45">OCTON's own rolling Brier on graded predictions: <span className="font-mono text-white/70">{st.octon_brier}</span> · next run {fmt(st?.next_due)}</p>}
      {st?.calibration && (
        <div className="mt-3 rounded-xl border border-white/8 bg-black/30 p-3" data-testid="calibration-curve"
          data-octon-section="reliability calibration curve bins ece predicted vs observed" data-octon-label="Reliability Curve" data-octon-state="ready">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-tac text-[9px] tracking-widest text-white/40">RELIABILITY CURVE · PREDICTED VS OBSERVED · {st.calibration.n_outcomes} OUTCOMES</p>
            {st.calibration.ece != null && <span className="font-mono text-[11px] text-white/70" data-testid="calibration-ece">ECE {st.calibration.ece}</span>}
          </div>
          <div className="mt-2 grid grid-cols-5 gap-2">
            {st.calibration.bins.map((b) => (
              <div key={b.range} className="text-center" data-testid={`calibration-bin-${b.range.replace(/[^0-9]+/g, "-")}`}>
                <div className="flex h-16 items-end justify-center gap-1">
                  <div className="w-3 rounded-t bg-white/25" style={{ height: `${Math.round((b.mean_predicted || 0) * 100)}%` }} title={`predicted ${b.mean_predicted ?? "—"}`} />
                  <div className="w-3 rounded-t bg-cyan-400/80" style={{ height: `${Math.round((b.observed || 0) * 100)}%` }} title={`observed ${b.observed ?? "—"}`} />
                </div>
                <p className="mt-1 font-mono text-[9px] text-white/50">{b.range}</p>
                <p className="font-mono text-[9px] text-white/35">n={b.n}{b.gap != null ? ` · ${b.gap > 0 ? "+" : ""}${Math.round(b.gap * 100)}` : ""}</p>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-white/40"><span className="inline-block h-2 w-2 rounded-sm bg-white/25" /> predicted · <span className="inline-block h-2 w-2 rounded-sm bg-cyan-400/80" /> observed · {st.calibration.read}</p>
        </div>
      )}
      {last.matches?.length ? <p className="mt-2 truncate text-[11px] text-white/40">Latest absorbed: {last.matches.slice(-3).join(" · ")}</p> : null}
    </div>
  );
};
